import { useState, useEffect, useMemo } from "react";
import MainLayout from "../layout/MainLayout";
import { useAuth } from "../context/AuthContext";
import RequestFormModal from "../components/requests/RequestFormModal";
import RequestViewModal from "../components/requests/RequestViewModal";
import {
    getHiringRequests,
    createHiringRequest,
    createArchivedHiringRequest,
    updateHiringRequest,
    updateHiringRequestStatus,
    getHiringRequestHistory,
    createVacancyFromHiringRequest,
    publishHiringRequestToHH,
    HIRING_REQUEST_STATUS,
    canCreateHiringRequest,
    canGenerateHiringRequestInvite,
    createHiringRequestInvite,
} from "../services/hiringRequestsApi";
import { DEPARTMENTS } from "../config/api";
import VacancyFilterStepModal from "../components/vacancies/VacancyFilterStepModal";
import { formatDateRu } from "../utils/dateFormat";
import {
    getHiringRequestDashboardStats,
    getHiringRequestStatusMeta,
    matchesHiringRequestSearch,
} from "../utils/hiringRequestPresentation";

const STATUS_CLASSES = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    orange: "border-orange-200 bg-orange-50 text-orange-700",
    red: "border-red-200 bg-red-50 text-red-700",
    slate: "border-slate-200 bg-slate-100 text-slate-700",
};

const deadlineCardClass = (state) => {
    if (state === "overdue") return "border-red-200 ring-1 ring-red-100";
    if (state === "warning") return "border-amber-200 ring-1 ring-amber-100";
    return "border-slate-200";
};

// Форматирование зарплаты
const formatSalary = (from, to) => {
    if (!from && !to) return "—";
    if (from && to) return `${from.toLocaleString()} - ${to.toLocaleString()}`;
    if (from) return `от ${from.toLocaleString()}`;
    return `до ${to.toLocaleString()}`;
};

export default function RequestsPage() {
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Модальные окна
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    // Состояния загрузки для операций
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isCreatingVacancy, setIsCreatingVacancy] = useState(false);
    const [isPublishingHH, setIsPublishingHH] = useState(false);
    const [filterStepVacancy, setFilterStepVacancy] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);
    const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);

    // Фильтры
    const [statusFilter, setStatusFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const stats = useMemo(() => getHiringRequestDashboardStats(requests), [requests]);
    const visibleRequests = useMemo(
        () => requests.filter((request) => matchesHiringRequestSearch(request, searchQuery)),
        [requests, searchQuery]
    );

    // Загрузка списка заявок
    const loadRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getHiringRequests(statusFilter || null, departmentFilter || null);
            setRequests(data || []);
        } catch (err) {
            setError(err.message || "Не удалось загрузить заявки");
            console.error("Error loading requests:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, departmentFilter]);

    // Создание заявки
    const handleCreateRequest = async (formData) => {
        try {
            setIsCreating(true);
            const newRequest = await createHiringRequest(formData);
            setRequests(prev => [newRequest, ...prev]);
            setShowFormModal(false);
        } catch (err) {
            alert(`Ошибка создания заявки: ${err.message}`);
            console.error("Error creating request:", err);
        } finally {
            setIsCreating(false);
        }
    };

    const handleArchiveRequest = async (formData) => {
        try {
            setIsCreating(true);
            const archivedRequest = await createArchivedHiringRequest(formData);
            setRequests(prev => [archivedRequest, ...prev]);
            setShowFormModal(false);
        } catch (err) {
            alert(`Ошибка сохранения заявки в архив: ${err.message}`);
            console.error("Error archiving request:", err);
        } finally {
            setIsCreating(false);
        }
    };

    // Обновление статуса
    const handleStatusChange = async (requestId, newStatus, details = {}) => {
        try {
            setIsUpdatingStatus(true);
            const updated = await updateHiringRequestStatus(requestId, newStatus, details);
            setRequests(prev => prev.map(r => r.id === requestId ? updated : r));
            if (selectedRequest?.id === requestId) {
                setSelectedRequest(updated);
            }
        } catch (err) {
            alert(`Ошибка обновления статуса: ${err.message}`);
            console.error("Error updating status:", err);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // Просмотр заявки
    const handleViewRequest = async (request) => {
        setSelectedRequest(request);
        setShowViewModal(true);
        try {
            const history = await getHiringRequestHistory(request.id);
            setSelectedRequest(prev => prev?.id === request.id ? { ...prev, history: history || [] } : prev);
        } catch (_) {
            setSelectedRequest(prev => prev?.id === request.id ? { ...prev, history: [] } : prev);
        }
    };

    const handleEditRequest = async (formData) => {
        if (!editingRequest?.id) return;
        try {
            setIsCreating(true);
            const updated = await updateHiringRequest(editingRequest.id, formData);
            setRequests(prev => prev.map(item => item.id === updated.id ? updated : item));
            setEditingRequest(null);
            setShowFormModal(false);
        } catch (err) {
            alert(`Ошибка сохранения заявки: ${err.message}`);
        } finally {
            setIsCreating(false);
        }
    };

    const handleCreateVacancy = async (requestId) => {
        try {
            setIsCreatingVacancy(true);
            const vacancy = await createVacancyFromHiringRequest(requestId);
            await loadRequests();
            setSelectedRequest((prev) =>
                prev?.id === requestId
                    ? { ...prev, linked_vacancy_id: vacancy.id, status: prev.status === "создана" || prev.status === "на анализе" ? "утверждена" : prev.status }
                    : prev
            );
            alert(
                `Вакансия #${vacancy.id} создана. Откройте её в «Вакансии», укажите роль и город, затем публикуйте на HH.`
            );
            if (vacancy?.id) {
                setFilterStepVacancy(vacancy);
            }
        } catch (err) {
            alert(`Не удалось создать вакансию: ${err.message}`);
        } finally {
            setIsCreatingVacancy(false);
        }
    };

    const handlePublishHH = async (requestId) => {
        try {
            setIsPublishingHH(true);
            const result = await publishHiringRequestToHH(requestId);
            await loadRequests();
            if (result?.hiring_request) {
                setSelectedRequest(result.hiring_request);
            }
            alert(
                result?.hh_vacancy_url
                    ? `Опубликовано на HH: ${result.hh_vacancy_url}`
                    : "Заявка опубликована на HH.ru"
            );
        } catch (err) {
            alert(`Публикация на HH не удалась: ${err.message}`);
        } finally {
            setIsPublishingHH(false);
        }
    };

    const handleGenerateInvite = async () => {
        try {
            setIsGeneratingInvite(true);
            setError(null);
            const result = await createHiringRequestInvite();
            setInviteLink(result);
            try { await navigator.clipboard.writeText(result.url); } catch { /* manual copy remains available */ }
        } catch (err) {
            setError(err.message || "Не удалось создать ссылку");
        } finally {
            setIsGeneratingInvite(false);
        }
    };

    const copyInvite = async () => {
        if (!inviteLink?.url) return;
        try {
            await navigator.clipboard.writeText(inviteLink.url);
        } catch {
            window.prompt("Скопируйте ссылку", inviteLink.url);
        }
    };

    return (
        <MainLayout>
            <div className="mb-7 space-y-5">
                <section className="relative overflow-hidden rounded-3xl bg-slate-950 px-6 py-7 text-white shadow-xl shadow-slate-200/60 md:px-8">
                    <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#cda834]/20 blur-3xl" />
                    <div className="relative flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
                        <div className="max-w-2xl">
                            <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#e4c45d]">
                                <span className="h-2 w-2 rounded-full bg-[#e4c45d]" />
                                Центр управления подбором
                            </div>
                            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Заявки на подбор</h1>
                            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 md:text-base">
                                Вся потребность компании в сотрудниках: ответственные, сроки, свободные места и следующий шаг по каждой заявке.
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                        {canGenerateHiringRequestInvite(user?.role) && (
                            <button
                                type="button"
                                onClick={handleGenerateInvite}
                                disabled={isGeneratingInvite}
                                className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white backdrop-blur hover:bg-white/15 disabled:opacity-50"
                            >
                                {isGeneratingInvite ? "Создаём ссылку…" : "Ссылка для руководителя"}
                            </button>
                        )}
                        <button
                            onClick={loadRequests}
                            disabled={loading}
                            className="rounded-xl border border-white/20 bg-white/10 p-3 text-white hover:bg-white/15 disabled:opacity-50"
                            title="Обновить"
                        >
                            <svg className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        </button>
                        
                        {canCreateHiringRequest(user?.role) && (
                            <button
                                onClick={() => setShowFormModal(true)}
                                className="flex items-center gap-2 rounded-xl bg-[#d9b743] px-5 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-black/20 hover:bg-[#e6c75b]"
                                data-testid="create-hiring-request"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                Создать заявку
                            </button>
                        )}
                        </div>
                    </div>
                </section>

                <section className="grid grid-cols-2 gap-3 xl:grid-cols-5" aria-label="Сводка по заявкам">
                    {[
                        { label: "Всего заявок", value: stats.total, accent: "text-slate-900", hint: "в выбранной выборке" },
                        { label: "В работе", value: stats.active, accent: "text-blue-700", hint: "требуют действий" },
                        { label: "Свободных мест", value: stats.freePlaces, accent: "text-violet-700", hint: "ещё нужно закрыть" },
                        { label: "Просрочено", value: stats.overdue, accent: "text-red-600", hint: "вышли за срок" },
                        { label: "На уточнении", value: stats.needsClarification, accent: "text-amber-700", hint: "ожидают руководителя" },
                    ].map((item) => (
                        <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</p>
                            <p className={`mt-2 text-3xl font-bold ${item.accent}`}>{item.value}</p>
                            <p className="mt-1 text-xs text-slate-500">{item.hint}</p>
                        </div>
                    ))}
                </section>

                {inviteLink ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900">Ссылка для создания заявки</p>
                                <p className="mt-1 text-xs text-slate-600">Одноразовая ссылка действует до {new Date(inviteLink.expires_at).toLocaleString("ru-RU")}.</p>
                                <input readOnly value={inviteLink.url} onFocus={(event) => event.target.select()} className="mt-2 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-700" />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={copyInvite} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white">Копировать</button>
                                <button type="button" onClick={() => setInviteLink(null)} className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-white">Закрыть</button>
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-end">
                    <div className="min-w-0 flex-1">
                        <label htmlFor="request-search" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Поиск</label>
                        <div className="relative">
                            <svg className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.35-5.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" /></svg>
                            <input id="request-search" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Должность, номер, отдел, руководитель или HR" className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-11 pr-4 text-sm outline-none transition focus:border-[#cda834] focus:bg-white focus:ring-2 focus:ring-[#cda834]/20" />
                        </div>
                    </div>
                    <div className="w-full sm:w-56 relative">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Статус</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all appearance-none cursor-pointer"
                        >
                            <option value="">Все статусы</option>
                            {Object.entries(HIRING_REQUEST_STATUS).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                        <div className="absolute bottom-3 right-3 pointer-events-none text-slate-400">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                    </div>

                    {(user?.role === "hr" || user?.role === "owner" || user?.role === "dev") && (
                        <div className="w-full sm:w-56 relative">
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Отдел</label>
                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all appearance-none cursor-pointer"
                            >
                                <option value="">Все отделы</option>
                                {DEPARTMENTS.map((dept) => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                            <div className="absolute bottom-3 right-3 pointer-events-none text-slate-400">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>{error}</div>
                    <button
                        type="button"
                        onClick={loadRequests}
                        className="shrink-0 px-4 py-2 bg-white border border-red-200 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
                    >
                        Повторить
                    </button>
                </div>
            )}

            {loading && requests.length === 0 ? (
                <div className="flex justify-center items-center py-20 text-slate-400">
                    <svg className="animate-spin h-8 w-8 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Загрузка заявок...
                </div>
            ) : visibleRequests.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200/60 text-center flex flex-col items-center">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                    </div>
                    <h3 className="text-lg font-medium text-slate-800">Заявок не найдено</h3>
                    <p className="text-slate-500 mt-1">
                        {searchQuery
                            ? "По вашему запросу ничего не найдено. Измените поисковую фразу или фильтры."
                            : statusFilter || departmentFilter
                            ? "Нет заявок с выбранными параметрами."
                            : "Пока нет ни одной заявки."}
                    </p>
                    {canCreateHiringRequest(user?.role) && !statusFilter && (
                        <button
                            type="button"
                            onClick={() => setShowFormModal(true)}
                            className="mt-4 bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800"
                        >
                            Создать первую заявку
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                    {visibleRequests.map((request) => {
                        const statusMeta = getHiringRequestStatusMeta(request.status);
                        const totalPlaces = Number(request.headcount) || 0;
                        const closedPlaces = Number(request.slot_summary?.closed) || 0;
                        const progress = totalPlaces > 0 ? Math.min(100, Math.round((closedPlaces / totalPlaces) * 100)) : 0;
                        return (
                        <div
                            key={request.id}
                            onClick={() => handleViewRequest(request)}
                            onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") handleViewRequest(request); }}
                            role="button"
                            tabIndex={0}
                            className={`group overflow-hidden rounded-3xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#cda834] ${deadlineCardClass(request.deadline_state)}`}
                        >
                            <div className="p-5 md:p-6">
                                <div className="mb-5 flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
                                            {request.public_code || `З-${request.id}`}
                                            {request.background_search && <span className="rounded-full bg-indigo-50 px-2 py-1 text-[10px] tracking-normal text-indigo-700">Фоновый подбор</span>}
                                        </div>
                                        <h3 className="truncate text-xl font-bold leading-tight text-slate-950 md:text-2xl">{request.position || "Должность не указана"}</h3>
                                        <p className="mt-1.5 text-sm font-medium text-slate-500">{request.department || "Отдел не указан"}</p>
                                    </div>
                                    <span className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-bold ${STATUS_CLASSES[statusMeta.tone] || STATUS_CLASSES.slate}`}>
                                        {statusMeta.label}
                                    </span>
                                </div>

                                <div className="mb-5 rounded-2xl bg-slate-50 p-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Прогресс закрытия</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-700">{closedPlaces} из {totalPlaces || "—"} мест закрыто</p>
                                        </div>
                                        <span className="text-2xl font-bold text-slate-900">{progress}%</span>
                                    </div>
                                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                                        <div className="h-full rounded-full bg-gradient-to-r from-[#c29e2f] to-[#e4c45d] transition-all" style={{ width: `${progress}%` }} />
                                    </div>
                                </div>

                                {request.slot_summary && (
                                    <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                                        {[
                                            ["Свободно", request.slot_summary.free || 0, "text-slate-800", "bg-slate-50"],
                                            ["Запланировано", request.slot_summary.planned || 0, "text-blue-700", "bg-blue-50"],
                                            ["На адаптации", request.slot_summary.adapting || 0, "text-amber-700", "bg-amber-50"],
                                            ["Закрыто", request.slot_summary.closed || 0, "text-emerald-700", "bg-emerald-50"],
                                        ].map(([label, value, color, background]) => (
                                            <div key={label} className={`rounded-xl px-3 py-2.5 ${background}`}>
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                                                <p className={`mt-1 text-xl font-bold ${color}`}>{value}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
                                    <div>
                                        <p className="text-xs text-slate-400">Руководитель</p>
                                        <p className="mt-1 truncate font-semibold text-slate-800">{request.initiator_name || request.manager_name || "Не назначен"}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Ответственный HR</p>
                                        <p className="mt-1 truncate font-semibold text-slate-800">{request.assigned_hr_name || "Не назначен"}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Условия</p>
                                        <p className="mt-1 font-semibold text-slate-800">{formatSalary(request.salary_from, request.salary_to)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Плановый срок</p>
                                        <p className={`mt-1 font-semibold ${
                                            request.deadline_state === "overdue" ? "text-red-600" :
                                             request.deadline_state === "warning" ? "text-amber-600" :
                                             "text-slate-800"
                                        }`}>
                                            {formatDateRu(request.planned_close_date, "Не указан")}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-slate-50/70 px-5 py-4 md:px-6">
                                <div className="min-w-0">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Следующий шаг</p>
                                    <p className="mt-0.5 truncate text-sm font-medium text-slate-700">{statusMeta.nextAction}</p>
                                </div>
                                <span className="flex shrink-0 items-center gap-1 text-sm font-bold text-[#a98518] transition group-hover:translate-x-0.5">
                                    Открыть
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </span>
                            </div>
                        </div>
                    )})}
                </div>
            )}

            {/* Модалка создания */}
            {showFormModal && (
                <RequestFormModal
                    onClose={() => { setShowFormModal(false); setEditingRequest(null); }}
                    onSubmit={editingRequest ? handleEditRequest : handleCreateRequest}
                    onArchive={editingRequest ? undefined : handleArchiveRequest}
                    isLoading={isCreating}
                    initialData={editingRequest}
                />
            )}

            {/* Модалка просмотра */}
            {showViewModal && (
                <RequestViewModal
                    request={selectedRequest}
                    onClose={() => {
                        setShowViewModal(false);
                        setSelectedRequest(null);
                    }}
                    onStatusChange={handleStatusChange}
                    onEdit={(request) => { setEditingRequest(request); setShowViewModal(false); setShowFormModal(true); }}
                    onCreateVacancy={handleCreateVacancy}
                    onPublishHH={handlePublishHH}
                    isUpdating={isUpdatingStatus}
                    isCreatingVacancy={isCreatingVacancy}
                    isPublishingHH={isPublishingHH}
                />
            )}

            {filterStepVacancy?.id && (
                <VacancyFilterStepModal
                    vacancy={filterStepVacancy}
                    onDone={() => setFilterStepVacancy(null)}
                    onSkip={() => setFilterStepVacancy(null)}
                />
            )}
        </MainLayout>
    );
}
